import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../supabase'
import { useAuth } from '../context/AuthContext'
import Patients from './Patients'
import Appointments from './Appointments'
import Inventory from './Inventory'
import Staff from './Staff'
import Reports from './Reports'
import Prescriptions from './Prescriptions'
import Billing from './Billing'
import Reminders, { fetchTomorrowPendingCount } from './Reminders'
import Feedback from './Feedback'
import Settings from './Settings'
import { invPaid, invBalance } from '../lib/billing'
import { friendlyError, canSeeTab } from '../lib/permissions'
import NoAccess from './NoAccess'

// ─── Design tokens ────────────────────────────────────────────────────────────
const T = {
  teal950: '#082828', teal900: '#0d3d3d', teal800: '#0f5454',
  teal700: '#126868', teal600: '#148080', teal500: '#19a8a8',
  teal400: '#2ecece', teal200: '#90e0e0', teal100: '#ccf2f2', teal50: '#edfafa',
  white: '#ffffff',
  gray50: '#f8fafc', gray100: '#f1f5f9', gray200: '#e2e8f0',
  gray300: '#cbd5e1', gray400: '#94a3b8', gray500: '#64748b',
  gray600: '#475569', gray700: '#334155', gray900: '#0f172a',
  red50: '#fff1f2', red100: '#ffe4e6', red600: '#dc2626',
  green50: '#f0fdf4', green600: '#16a34a',
  amber50: '#fffbeb', amber600: '#d97706',
  font: "'Segoe UI', system-ui, -apple-system, sans-serif",
}

// ─── Nav tabs ─────────────────────────────────────────────────────────────────
const TABS = [
  { id: 'dashboard',    label: 'Dashboard',    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg> },
  { id: 'appointments', label: 'Appointments', icon: '📅' },
  { id: 'billing',      label: 'Billing',      icon: '🧾' },
  { id: 'patients',     label: 'Patients',     icon: '👤' },
  { id: 'inventory',    label: 'Inventory',    icon: '📦' },
  { id: 'staff',        label: 'Staff',        icon: '👥' },
  { id: 'reports',       label: 'Reports',       icon: '📊' },
  { id: 'prescriptions', label: 'Prescriptions', icon: '📋' },
  { id: 'reminders',    label: 'Reminders',    icon: '🔔' },
  { id: 'feedback',      label: 'Feedback',      icon: '💬' },
  { id: 'settings',     label: 'Settings',     icon: '⚙️' },
]

// ─── IST greeting ─────────────────────────────────────────────────────────────
function getGreeting() {
  const now = new Date()
  const istTime = new Date(now.getTime() + (now.getTimezoneOffset() * 60 * 1000) + 5.5 * 60 * 60 * 1000)
  const hour = istTime.getHours()
  if (hour >= 5 && hour < 12) return 'Good morning! 🌅'
  if (hour >= 12 && hour < 17) return 'Good afternoon! ☀️'
  if (hour >= 17 && hour < 21) return 'Good evening! 🌇'
  return 'Good night! 🌙'
}

// ─── Status badge colours ─────────────────────────────────────────────────────
const STATUS_COLORS = {
  Scheduled:  { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
  Confirmed:  { bg: T.teal50,  color: T.teal700, border: T.teal200 },
  Completed:  { bg: T.green50, color: T.green600, border: '#bbf7d0' },
  Cancelled:  { bg: T.red50,   color: T.red600,  border: '#fecdd3' },
  'No Show':  { bg: T.amber50, color: T.amber600, border: '#fde68a' },
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function todayISO() {
  // toISOString() is always UTC — at 00:30 IST it returns yesterday's date.
  // Build YYYY-MM-DD from local date parts so it matches the device's calendar date.
  const d = new Date()
  const yyyy = d.getFullYear()
  const mm   = String(d.getMonth() + 1).padStart(2, '0')
  const dd   = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}
function fmtTime(t) {
  if (!t) return '—'
  const [h, m] = t.split(':')
  const hr = parseInt(h, 10)
  return `${hr % 12 || 12}:${m} ${hr < 12 ? 'AM' : 'PM'}`
}
function fmtCurrency(n) {
  return '₹' + (Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 0 })
}

// ─── Stat card ────────────────────────────────────────────────────────────────
function StatCard({ icon, label, value, sub, accent }) {
  return (
    <div style={{
      background: T.white, borderRadius: 14, padding: '22px 24px',
      boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
      borderLeft: `4px solid ${accent}`,
      display: 'flex', flexDirection: 'column', gap: 6, flex: 1, minWidth: 0,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: T.gray500, letterSpacing: '0.3px' }}>
          {label}
        </span>
        <span style={{
          fontSize: 20, width: 38, height: 38, borderRadius: 10,
          background: accent + '18', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>{icon}</span>
      </div>
      <div style={{ fontSize: 28, fontWeight: 800, color: T.gray900, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: T.gray400 }}>{sub}</div>}
    </div>
  )
}

// ─── Status badge ─────────────────────────────────────────────────────────────
function Badge({ status }) {
  const c = STATUS_COLORS[status] || { bg: T.gray100, color: T.gray600, border: T.gray200 }
  return (
    <span style={{
      background: c.bg, color: c.color, border: `1px solid ${c.border}`,
      borderRadius: 20, padding: '2px 10px', fontSize: 11.5, fontWeight: 600,
    }}>{status}</span>
  )
}


// ─── Dashboard home ───────────────────────────────────────────────────────────
function DashboardHome() {
  // Receptionists see today's operational figures only — no revenue totals
  const { role } = useAuth()
  const isReception = role === 'receptionist'
  const [stats, setStats]         = useState({ patients: 0, revenue: 0, pending: 0, next: null })
  const [appointments, setAppts]  = useState([])
  const [loading, setLoading]     = useState(true)
  const [error, setError]         = useState('')

  const loadData = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const today = todayISO()

      // Today's appointments (with patient name)
      const { data: appts, error: apptErr } = await supabase
        .from('appointments')
        .select('*, patients(full_name, phone)')
        .eq('date', today)
        .order('time', { ascending: true })

      if (apptErr) throw apptErr

      // Invoices raised today (IST day boundaries)
      const { data: invs, error: invErr } = await supabase
        .from('invoices')
        .select('total, amount_paid, balance_due, payment_status, status')
        .gte('created_at', `${today}T00:00:00+05:30`)
        .lte('created_at', `${today}T23:59:59.999+05:30`)

      if (invErr) throw invErr

      // Collected = money actually received (incl. partial payments); pending = balance still due
      const revenue  = (invs || []).reduce((s, i) => s + invPaid(i), 0)
      const pending  = (invs || []).reduce((s, i) => s + invBalance(i), 0)

      const upcoming = (appts || []).find(a => a.status === 'Scheduled' || a.status === 'Confirmed')

      setAppts(appts || [])
      setStats({
        patients: (appts || []).length,
        revenue,
        pending,
        next: upcoming || null,
      })
    } catch (e) {
      setError(friendlyError(e, 'Failed to load data'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadData() }, [loadData])

  const today = new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })

  return (
    <div style={{ padding: '28px 32px', fontFamily: T.font, flex: 1, overflowY: 'auto' }}>

      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: T.gray900, margin: 0 }}>{getGreeting()}</h1>
          <p style={{ fontSize: 13, color: T.gray400, margin: '4px 0 0' }}>{today}</p>
        </div>
        <button onClick={loadData} style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '8px 16px', borderRadius: 8, border: `1.5px solid ${T.gray200}`,
          background: T.white, color: T.gray600, fontSize: 13, fontWeight: 600,
          cursor: 'pointer', fontFamily: T.font,
        }}>
          🔄 Refresh
        </button>
      </div>

      {error && (
        <div style={{
          background: T.red50, border: `1px solid #fecdd3`, borderRadius: 10,
          padding: '12px 16px', color: T.red600, fontSize: 13, marginBottom: 20,
        }}>
          ⚠️ {error}
        </div>
      )}

      {/* Stat cards */}
      <div style={{ display: 'flex', gap: 18, marginBottom: 28, flexWrap: 'wrap' }}>
        <StatCard
          icon="👤" label="Patients Today"
          value={loading ? '—' : stats.patients}
          sub="Total appointments today"
          accent={T.teal500}
        />
        <StatCard
          icon="💰" label={isReception ? "Today's Collection" : 'Revenue Today'}
          value={loading ? '—' : fmtCurrency(stats.revenue)}
          sub="Collected on today's invoices"
          accent="#8b5cf6"
        />
        {!isReception && (
          <StatCard
            icon="⏳" label="Pending Payments"
            value={loading ? '—' : fmtCurrency(stats.pending)}
            sub="Balance due on today's invoices"
            accent={T.amber600}
          />
        )}
        <StatCard
          icon="🕐" label="Next Appointment"
          value={loading ? '—' : stats.next ? fmtTime(stats.next.time) : 'None'}
          sub={stats.next ? `${stats.next.patients?.full_name || 'Patient'} · ${stats.next.service_type}` : 'No upcoming today'}
          accent={T.green600}
        />
      </div>

      {/* Today's appointments table */}
      <div style={{
        background: T.white, borderRadius: 14,
        boxShadow: '0 2px 12px rgba(0,0,0,0.07)', overflow: 'hidden',
      }}>
        <div style={{
          padding: '18px 24px', borderBottom: `1px solid ${T.gray100}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <h2 style={{ fontSize: 15, fontWeight: 700, color: T.gray900, margin: 0 }}>
              Today's Appointments
            </h2>
            <p style={{ fontSize: 12, color: T.gray400, margin: '3px 0 0' }}>
              {loading ? 'Loading…' : `${appointments.length} appointment${appointments.length !== 1 ? 's' : ''} scheduled`}
            </p>
          </div>
          <span style={{
            background: T.teal50, color: T.teal700, border: `1px solid ${T.teal100}`,
            borderRadius: 20, padding: '3px 12px', fontSize: 12, fontWeight: 600,
          }}>
            {todayISO()}
          </span>
        </div>

        {loading ? (
          <div style={{ padding: '40px 24px', textAlign: 'center', color: T.gray400, fontSize: 14 }}>
            Loading appointments…
          </div>
        ) : appointments.length === 0 ? (
          <div style={{ padding: '48px 24px', textAlign: 'center' }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>📅</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: T.gray600 }}>No appointments today</div>
            <div style={{ fontSize: 13, color: T.gray400, marginTop: 4 }}>Schedule a new appointment to get started</div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: T.gray50 }}>
                  {['Time', 'Patient', 'Phone', 'Service', 'Doctor', 'Status', !isReception && 'Notes'].filter(Boolean).map(h => (
                    <th key={h} style={{
                      padding: '10px 16px', textAlign: 'left',
                      fontSize: 11.5, fontWeight: 700, color: T.gray500,
                      letterSpacing: '0.5px', textTransform: 'uppercase',
                      borderBottom: `1px solid ${T.gray200}`,
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {appointments.map((a, i) => (
                  <tr key={a.id} style={{
                    background: i % 2 === 0 ? T.white : T.gray50,
                    borderBottom: `1px solid ${T.gray100}`,
                    transition: 'background 0.1s',
                  }}
                    onMouseEnter={e => e.currentTarget.style.background = T.teal50}
                    onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? T.white : T.gray50}
                  >
                    <td style={{ padding: '13px 16px', fontWeight: 700, color: T.teal700, whiteSpace: 'nowrap' }}>
                      {fmtTime(a.time)}
                    </td>
                    <td style={{ padding: '13px 16px', fontWeight: 600, color: T.gray900 }}>
                      {a.patients?.full_name || '—'}
                    </td>
                    <td style={{ padding: '13px 16px', color: T.gray500 }}>
                      {a.patients?.phone || '—'}
                    </td>
                    <td style={{ padding: '13px 16px', color: T.gray700 }}>
                      {a.service_type}
                    </td>
                    <td style={{ padding: '13px 16px', color: T.gray700 }}>
                      {a.doctor}
                    </td>
                    <td style={{ padding: '13px 16px' }}>
                      <Badge status={a.status} />
                    </td>
                    {!isReception && <td style={{ padding: '13px 16px', color: T.gray400, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {a.notes || '—'}
                    </td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Main Dashboard shell ─────────────────────────────────────────────────────
export default function Dashboard() {
  const { session, logout, clinic, userProfile } = useAuth()
  const [activeTab, setActiveTab] = useState('dashboard')
  const [loggingOut, setLoggingOut] = useState(false)
  const [reminderCount, setReminderCount] = useState(0)

  const userEmail = session?.user?.email || ''

  // Poll reminder count every 90 seconds
  useEffect(() => {
    async function refresh() {
      try {
        const count = await fetchTomorrowPendingCount()
        setReminderCount(count)
      } catch { /* silently ignore */ }
    }
    refresh()
    const id = setInterval(refresh, 90_000)
    return () => clearInterval(id)
  }, [])

  async function handleLogout() {
    setLoggingOut(true)
    try { await logout() } catch { setLoggingOut(false) }
  }

  const role = userProfile?.role || null
  const visibleTabs = TABS.filter(t => canSeeTab(role, t.id))

  function renderContent() {
    // Guard against a hidden section being opened some other way (e.g. a stale tab state)
    if (!canSeeTab(role, activeTab)) return <NoAccess />
    switch (activeTab) {
      case 'dashboard':    return <DashboardHome />
      case 'appointments': return <Appointments />
      case 'billing':      return <Billing />
      case 'patients':     return <Patients />
      case 'inventory':    return <Inventory />
      case 'staff':        return <Staff />
      case 'reports':        return <Reports />
      case 'prescriptions':  return <Prescriptions />
      case 'reminders':    return <Reminders />
      case 'feedback':       return <Feedback />
      case 'settings':     return <Settings />
      default:             return <DashboardHome />
    }
  }

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100vh',
      fontFamily: T.font, background: T.gray100, overflow: 'hidden',
    }}>

      {/* ── Top bar ── */}
      <header style={{
        background: T.teal900, flexShrink: 0,
        boxShadow: '0 2px 16px rgba(0,0,0,0.28)', zIndex: 50,
      }}>

        {/* Row 1: brand + user + logout */}
        <div style={{
          display: 'flex', alignItems: 'center', padding: '0 24px',
          height: 56, gap: 14,
        }}>
          {/* Brand */}
          <div style={{display:'flex', flexDirection:'column', justifyContent:'center', minWidth:'200px', paddingRight:'40px'}}>
            <img src="/mysa-desk-logo-white.svg" alt="Mysa Desk" height="48" style={{display:'block'}} />
          </div>

          <div style={{ flex: 1 }} />

          {/* User email */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '5px 12px', background: 'rgba(255,255,255,0.08)',
            borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)',
          }}>
            <div style={{
              width: 26, height: 26, borderRadius: '50%', background: T.teal600,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 12, fontWeight: 700, color: T.white,
            }}>
              {userEmail.charAt(0).toUpperCase()}
            </div>
            <span style={{ fontSize: 12.5, color: T.teal100, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {userProfile?.full_name || userEmail}
            </span>
            {userProfile?.role && (
              <span style={{ fontSize: 10, fontWeight: 700, color: T.teal900, background: T.teal200, borderRadius: 10, padding: '1px 7px', textTransform: 'capitalize' }}>
                {userProfile.role}
              </span>
            )}
          </div>

          {/* Bell badge */}
          <button
            onClick={() => setActiveTab('reminders')}
            title="WhatsApp Reminders"
            style={{
              position: 'relative',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 38, height: 38, borderRadius: 8,
              background: reminderCount > 0 ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.09)',
              border: reminderCount > 0 ? '1px solid rgba(255,255,255,0.35)' : '1px solid rgba(255,255,255,0.15)',
              cursor: 'pointer', fontSize: 17,
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
            onMouseLeave={e => e.currentTarget.style.background = reminderCount > 0 ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.09)'}
          >
            🔔
            {reminderCount > 0 && (
              <span style={{
                position: 'absolute', top: -5, right: -5,
                background: '#ef4444', color: '#fff',
                fontSize: 10, fontWeight: 800,
                minWidth: 18, height: 18, borderRadius: 9,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '0 4px', lineHeight: 1,
                border: `2px solid ${T.teal900}`,
              }}>{reminderCount > 99 ? '99+' : reminderCount}</span>
            )}
          </button>

          {/* Logout */}
          <button
            onClick={handleLogout}
            disabled={loggingOut}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '7px 14px', borderRadius: 8,
              background: 'rgba(255,255,255,0.09)',
              border: '1px solid rgba(255,255,255,0.15)',
              color: T.teal100, fontSize: 13, fontWeight: 600,
              cursor: loggingOut ? 'not-allowed' : 'pointer',
              opacity: loggingOut ? 0.6 : 1, fontFamily: T.font,
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.16)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.09)'}
          >
            {loggingOut ? '…' : '⏻'} {loggingOut ? 'Logging out' : 'Logout'}
          </button>
        </div>

        {/* Row 2: nav tabs */}
        <nav style={{
          display: 'flex', alignItems: 'center',
          padding: '0 24px', gap: 2, overflowX: 'auto',
        }}>
          {clinic?.name && <span style={{fontSize:'11px', letterSpacing:'0.18em', color:'rgba(255,255,255,0.65)', textTransform:'uppercase', whiteSpace:'nowrap', paddingRight:'20px', borderRight:'1px solid rgba(255,255,255,0.2)', marginRight:'16px'}}>{clinic.name}</span>}
          {visibleTabs.map(tab => {
            const active = activeTab === tab.id
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '10px 16px 11px',
                  background: active ? T.gray100 : 'transparent',
                  border: 'none',
                  borderRadius: active ? '8px 8px 0 0' : '8px 8px 0 0',
                  color: active ? T.teal700 : T.teal200,
                  fontSize: 13, fontWeight: active ? 700 : 500,
                  cursor: 'pointer', fontFamily: T.font,
                  whiteSpace: 'nowrap',
                  transition: 'background 0.15s, color 0.15s',
                  borderBottom: active ? `none` : '2px solid transparent',
                }}
                onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.08)' }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent' }}
              >
                <span style={{ fontSize: 14 }}>{tab.icon}</span>
                {tab.label}
              </button>
            )
          })}
        </nav>
      </header>

      {/* ── Main content ── */}
      <main style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {renderContent()}
      </main>
    </div>
  )
}
